import * as React from "react";
import "./Collapsible.css";
import { Icon } from "office-ui-fabric-react";

type Props = {
    title: string;
    iconName?: string;
    expanded?: boolean;
    disabled?: boolean;
    collapsedChevronDirection?: ChevronDirection;
    classNames?: {
        head?: string;
        body?: string;
    };
    shouldDismount?: boolean;
    onToggle?: (expanded: boolean) => void;
};

export enum ChevronDirection {
    Up,
    Right,
}

export default function Collapsible(props: React.PropsWithChildren<Props>) {
    const { iconName, title, disabled, classNames, children, collapsedChevronDirection = ChevronDirection.Right, shouldDismount = true, onToggle } = props;

    const [expanded, setExpanded] = React.useState(props.expanded ?? false);
    React.useEffect(() => {
        setExpanded(props.expanded ?? false);
    }, [props.expanded]);

    const chevronDirectionClass = getChevronDirectionClass(collapsedChevronDirection);

    const toggleExpanded = () => {
        if (disabled) {
            return;
        }
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        onToggle?.(newExpanded);
    };

    const renderChildren = () => {
        if (shouldDismount) {
            return expanded ? children : null;
        }
        return children;
    };

    const getVisibilityClass = (): string => {
        if (expanded) {
            return "visible";
        }
        return shouldDismount ? "" : "hidden";
    };

    return (
        <div className={`accordion ${disabled ? "readonly" : ""} ${expanded ? "open" : ""}`}>
            <div
                className={`accordion-head ${classNames?.head ? classNames.head : ""}`}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={toggleExpanded}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded();
                    }
                }}
            >
                <span className={`chevron ${chevronDirectionClass}`}></span>
                <div className="accordion-head-title">
                    {iconName && <Icon iconName={iconName} />}
                    {title}
                </div>
            </div>
            <div className={`accordion-body ${classNames?.body ? classNames.body : ""} ${getVisibilityClass()}`}>{renderChildren()}</div>
        </div>
    );
}

function getChevronDirectionClass(direction: ChevronDirection): string {
    switch (direction) {
        case ChevronDirection.Up:
            return "chevron-up";
        case ChevronDirection.Right:
            return "chevron-right";
        default:
            return "chevron-right";
    }
}
